import { motion } from 'framer-motion';
import type { MotionStyle } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode, MouseEvent } from 'react';

type Variant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md' | 'lg';

// framer-motion redefines these handlers with animation-specific signatures,
// which conflict with the standard DOM event handler types from ButtonHTMLAttributes.
type MotionConflictingHandlers =
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd';

export interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  MotionConflictingHandlers
> {
  variant?: Variant;
  size?: Size;
  /** Show a spinner and disable interaction. */
  loading?: boolean;
  /** Stretch to the full width of the container. */
  block?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const cx = (...names: (string | false | undefined)[]): string => names.filter(Boolean).join(' ');

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  leadingIcon,
  trailingIcon,
  disabled,
  children,
  className,
  type = 'button',
  onClick,
  style,
  ...rest
}: ButtonProps): JSX.Element {
  const isDisabled = disabled === true || loading;

  const baseClasses =
    'relative inline-flex items-center justify-center whitespace-nowrap font-medium transition-all duration-300 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-page dark:focus:ring-offset-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed';

  const sizeClasses = {
    sm: 'text-[11px] px-3 py-1.5 gap-1.5',
    md: 'text-[13px] px-4 py-2 gap-2',
    lg: 'text-[14px] px-5 py-2.5 gap-2',
  };

  const variantClasses = {
    primary:
      'bg-brand-700 text-white hover:bg-brand-600 dark:bg-brand-600 dark:hover:bg-brand-500 shadow-sm dark:shadow-glow-brand focus:ring-brand-700 dark:focus:ring-brand-400',
    accent:
      'bg-brand-600 text-white hover:bg-brand-500 shadow-soft dark:shadow-glow-brand focus:ring-brand-500',
    secondary:
      'bg-white dark:bg-zinc-800/80 text-zinc-900 dark:text-zinc-200 border border-zinc-200 dark:border-white/10 shadow-sm dark:shadow-none hover:bg-zinc-50 dark:hover:bg-zinc-700/80 focus:ring-zinc-900 dark:focus:ring-zinc-500',
    ghost:
      'bg-transparent text-zinc-500 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/5 focus:ring-zinc-900 dark:focus:ring-zinc-500',
    danger:
      'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-500/20 focus:ring-rose-500',
    success:
      'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 focus:ring-emerald-500',
  };

  return (
    <motion.button
      type={type}
      className={cx(
        baseClasses,
        sizeClasses[size],
        variantClasses[variant],
        block && 'w-full',
        className,
      )}
      disabled={isDisabled}
      aria-busy={loading}
      whileHover={isDisabled ? {} : { scale: 1.02 }}
      whileTap={isDisabled ? {} : { scale: 0.98 }}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      {...(style !== undefined ? { style: style as MotionStyle } : {})}
      {...rest}
    >
      {loading && <Loader2 className="animate-spin w-4 h-4" />}
      {!loading && leadingIcon}
      {children}
      {!loading && trailingIcon}
    </motion.button>
  );
}
